/**
 * Admin Hub Layout Component
 * Main layout wrapper that includes sidebar, breadcrumbs, and content area
 */
import React, { useEffect } from 'react';
import { Box, Drawer, useMediaQuery, useTheme } from '@mui/material';
import { Outlet, useNavigate } from 'react-router-dom';
import { AdminHubProvider, useAdminHub } from './context/AdminHubContext';
import { AdminHubSidebar } from './AdminHubSidebar';
import { AdminHubBreadcrumbs } from './AdminHubBreadcrumbs';
import { AdminHubCommandPalette } from './AdminHubCommandPalette';
import { useCurrentUser, type UserInfo } from '../../hooks/useApi';
import { getAdminLayoutMode, ADMIN_HUB_DESIGN } from '../../config/adminHubConfig';

// Inner layout component that uses context
const AdminHubLayoutInner: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { sidebarCollapsed, setSidebarCollapsed } = useAdminHub();

  // On mobile, sidebar is hidden by default (shown via drawer)
  const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false);

  // Handle responsive sidebar
  useEffect(() => {
    if (isMobile) {
      setSidebarCollapsed(true);
    }
  }, [isMobile, setSidebarCollapsed]);

  const sidebarWidth = sidebarCollapsed
    ? ADMIN_HUB_DESIGN.SIDEBAR_WIDTH_COLLAPSED
    : ADMIN_HUB_DESIGN.SIDEBAR_WIDTH_EXPANDED;

  return (
    <Box
      sx={{
        display: 'flex',
        height: 'calc(100vh - 64px)', // Subtract header height (mt: 8 = 64px from Layout)
        overflow: 'hidden',
      }}
    >
      {/* Desktop Sidebar */}
      {!isMobile && <AdminHubSidebar />}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: ADMIN_HUB_DESIGN.SIDEBAR_WIDTH_EXPANDED,
              boxSizing: 'border-box',
              mt: '64px', // Account for header (mt: 8 = 64px from Layout)
              height: 'calc(100% - 64px)',
            },
          }}
        >
          <AdminHubSidebar isMobile />
        </Drawer>
      )}

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundColor: 'background.default',
        }}
      >
        {/* Breadcrumbs bar */}
        <AdminHubBreadcrumbs />

        {/* Content */}
        <Box
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            p: 3,
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {/* Command Palette */}
      <AdminHubCommandPalette />
    </Box>
  );
};

// Wrapper component that provides context
export const AdminHubLayout: React.FC = () => {
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser() as { data: UserInfo | null | undefined };

  const userPermissions = currentUser?.permissions ?? [];
  const layoutMode = getAdminLayoutMode(userPermissions);

  // Redirect if user has no admin access
  useEffect(() => {
    if (currentUser !== undefined && layoutMode === 'none') {
      navigate('/', { replace: true });
    }
  }, [currentUser, layoutMode, navigate]);

  // Don't render until we know the user's permissions
  if (currentUser === undefined || layoutMode === 'none') {
    return null;
  }

  // For config-only users, redirect to their specific section
  if (layoutMode === 'config-only') {
    // This will be handled by specific routes
    // ConfigOnlyLayout will be used instead
  }

  return (
    <AdminHubProvider userPermissions={userPermissions}>
      <AdminHubLayoutInner />
    </AdminHubProvider>
  );
};

export default AdminHubLayout;
